// src/pages/TemplateGallery.jsx
//
// The Template Gallery: every structure an author can start a draft from, as a
// grid of book covers, and — when one is previewed — that template's detail
// page in place of the grid.
//
// The Library's Templates tab: Library.jsx renders it in its main column when
// TEMPLATE GALLERY is chosen in the sidebar, the same way it shows Manuscripts
// and Covers, so choosing a template never leaves the Library. The old
// `/templates` address redirects there. A previewed template is in the URL
// (`/library?template=<id>`), so it can be linked to and the browser's Back
// button returns to the grid.
//
// Built to the 2026-11 gallery prototype, less its left panel and top bar —
// the Library's sidebar and the app's Navbar stay — and with the prototype's
// search box moved into the filter bar.
//
// **Persistence.** Nothing here writes. The catalogue is read from
// `GET /api/manuscript-templates`, seeded by
// `backend/tools/seed_manuscript_templates.py`; choosing a template hands its id
// to NewDocumentModal, and `POST /api/drafts` does the seeding server-side.
// "Popular" sorts by the `uses` count that GET carries.
//
// Colours come from `templateTokens.js`, which re-exports the mapping in
// `structureTokens.js` — the prototype's own palette is not used. The book
// covers are artwork and the one exception; see TemplateCover.jsx.
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';

import { API_BASE } from '../utils/api';
import TemplateFilterBar from '../components/templates/TemplateFilterBar';
import TemplateCard from '../components/templates/TemplateCard';
import TemplateDetail from '../components/templates/TemplateDetail';
import { SP, FONT, BP, FONT_DISPLAY, CATEGORY_LABEL } from '../components/templates/templateTokens';
import NewDocumentModal from '../components/editor/NewDocumentModal';

const HERO_IMAGE = '/images/templates-hero.jpg';

/** Does a template match what was typed in the search box? */
export function matchesQuery(template, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    template.name,
    template.description,
    CATEGORY_LABEL[template.category] || template.category,
    ...(template.tags || []),
  ].join(' ').toLowerCase();
  return haystack.includes(q);
}

/** The templates in the chosen order. Ties keep catalogue order. */
export function sortTemplates(templates, sort) {
  const list = [...templates];
  const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);
  if (sort === 'az') return list.sort((a, b) => a.name.localeCompare(b.name));
  if (sort === 'short') return list.sort((a, b) => (a.wordsMin ?? 0) - (b.wordsMin ?? 0) || byOrder(a, b));
  if (sort === 'long') return list.sort((a, b) => (b.wordsMin ?? 0) - (a.wordsMin ?? 0) || byOrder(a, b));
  return list.sort((a, b) => (b.uses ?? 0) - (a.uses ?? 0) || byOrder(a, b));
}

export default function TemplateGallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const detailId = searchParams.get('template');

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('popular');

  const [createOpen, setCreateOpen] = useState(false);
  const [chosenTemplateId, setChosenTemplateId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const accessToken = localStorage.getItem('rv_access_token');
        const res = await fetch(`${API_BASE}/api/manuscript-templates`, {
          credentials: 'omit',
          headers: {
            Accept: 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        });
        if (!res.ok) throw new Error(`Failed to load templates (${res.status})`);
        const data = await res.json();
        if (!cancelled) setTemplates(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const detail = useMemo(
    () => (detailId ? templates.find((t) => t.id === detailId) || null : null),
    [templates, detailId],
  );

  const visible = useMemo(
    () => sortTemplates(
      templates.filter((t) => (activeCategory === 'all' || t.category === activeCategory) && matchesQuery(t, query)),
      sort,
    ),
    [templates, activeCategory, query, sort],
  );

  // A new page, so it opens at its top — the grid may have been scrolled far down.
  useEffect(() => { window.scrollTo?.(0, 0); }, [detailId]);

  const openDetail = (id) => setSearchParams({ template: id });
  const closeDetail = () => setSearchParams({});

  // Not named `useTemplate`: anything starting with `use` is read as a hook by
  // react-hooks/rules-of-hooks, and passing it to a card's onUse is then a
  // build error.
  const chooseTemplate = (id) => {
    setChosenTemplateId(id);
    setCreateOpen(true);
  };

  return (
    // The Library's main column supplies the padding and the tinted ground.
    <Box sx={{ fontFamily: FONT.sans }}>
      <Box sx={{ maxWidth: 1320, mx: 'auto', pb: '32px' }}>
        {detail ? (
          <TemplateDetail
            template={detail}
            onBack={closeDetail}
            onUse={() => chooseTemplate(detail.id)}
          />
        ) : detailId && loading ? (
          // A link straight to a template: wait for it rather than flash the grid.
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '120px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* The hero. The prototype's top bar (search, bell, avatar) is the
                app's Navbar here, and its search box is in the filter bar. */}
            <Box
              sx={{
                position: 'relative',
                background: `linear-gradient(90deg, ${alpha(SP.purpleDeep, 0.92)} 0%, ${alpha(SP.purpleDeep, 0.65)} 55%, ${alpha(SP.purpleDeep, 0.25)} 100%), url(${HERO_IMAGE}) center / cover`,
                borderRadius: '16px',
                p: '40px 32px',
                color: SP.white,
                mb: '22px',
                [BP.panelFloats]: { p: '24px 18px 28px' },
              }}
            >
              <Typography
                component="h1"
                sx={{ fontFamily: FONT_DISPLAY, fontWeight: 600, fontSize: 38, lineHeight: 1.15, mb: '12px', color: SP.white, [BP.panelFloats]: { fontSize: 28 } }}
              >
                Choose a template.<br />Start your story.
              </Typography>
              <Typography sx={{ fontSize: 14, color: alpha(SP.white, 0.85), maxWidth: 520, lineHeight: 1.55 }}>
                Professional templates for every kind of writing — novels, scripts, stage plays, comics,
                academic papers and planning sheets — each set up in the exact format the industry expects.
              </Typography>
            </Box>

            {!loading && !error && templates.length > 0 && (
              <TemplateFilterBar
                templates={templates}
                active={activeCategory}
                onCategory={setActiveCategory}
                query={query}
                onQuery={setQuery}
                sort={sort}
                onSort={setSort}
              />
            )}

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: '80px' }}>
                <CircularProgress />
              </Box>
            )}

            {!loading && error && (
              <Typography sx={{ textAlign: 'center', color: SP.gray, py: '80px' }} data-testid="template-error">
                {error}
              </Typography>
            )}

            {/* The catalogue is seeded data, so "none yet" is a real state on a
                fresh environment rather than an error. */}
            {!loading && !error && templates.length === 0 && (
              <Typography sx={{ textAlign: 'center', color: SP.gray, py: '80px' }} data-testid="template-empty">
                No templates have been published yet.
              </Typography>
            )}

            {!loading && !error && templates.length > 0 && (
              <Box
                data-testid="template-grid"
                sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(232px, 1fr))', gap: '20px' }}
              >
                {visible.length === 0 && (
                  <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: '60px', color: SP.gray }} data-testid="template-no-match">
                    No templates match “{query.trim()}”.
                  </Box>
                )}
                {visible.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onPreview={() => openDetail(template.id)}
                    onUse={() => chooseTemplate(template.id)}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* The app's own Create dialog, not a copy of it — it arrives with the
          chosen template already selected and the author free to change it. */}
      <NewDocumentModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        initialTemplateId={chosenTemplateId}
      />
    </Box>
  );
}
